import parsePhoneNumberFromString from "libphonenumber-js/max";
import Papa from "papaparse";

import { ContactDto } from "@bcasmart/doxecure-commons/doxecureClientTypes";
import { $isemail, $isstring, $length, $ok } from "foundation-ts/commons";
import { $uuid } from "foundation-ts/crypto";
import { $ascii } from "foundation-ts/strings";
import { TSError } from "foundation-ts/tserrors";

import { NewPersonFormValues } from "@components/ContactForm/ContactForm";
import { ContactToImport, ImportContactStatus } from "@components/Import/types";
import { detectedCharset } from "@utils/detectCharset";
import { optlog } from "@utils/functions";

export const parseContactsCsv = async (file: File, contacts: ContactDto[], collaborators: string[]): Promise<ContactToImport[]> => {
    const text = await decodeFileToText(file);
    const result = Papa.parse<Record<string, string>>(text, {
        header: true,
        skipEmptyLines: true,
        transformHeader: (h) => h.toLowerCase().trim(),
    });
    if (result.errors.length > 0) {
        optlog("Erreurs de parsing CSV :", result.errors);
    }

    const regex = new RegExp(/^\s*[a-zàâäéèêëïîôöùûüç]+([ '-][a-zàâäéèêëïîôöùûüç]+)*\s*$/i);
    const headers = result.meta.fields ?? [];
    const mapping = buildColumnMapping(headers);
    const newcontacts: ContactToImport[] = [];
    for (const row of result.data) {
        let note: string|undefined = undefined;
        let status: ImportContactStatus = 'ok';

        const id = $uuid();
        const email = row[mapping.email]?.toLowerCase().trim();
        const lastname = row[mapping.lastname]?.toUpperCase().trim();
        const firstname = $ok(mapping.firstname) ? row[mapping.firstname!]?.capitalize().trim() : undefined;
        const phone = $ok(mapping.phone) ? row[mapping.phone!]?.trim() : undefined;
        const dataToReturn: NewPersonFormValues = {
            email: email ?? "",
            lastName: lastname ?? "",
            firstName: firstname ?? "",
            phone,
        };

        if ($length(email) === 0 || !$isemail(email)) {
            newcontacts.push({ data: { ...dataToReturn }, id, status: "excluded", note: $length(email) === 0 ? "L'email est obligatoire" : "L'email est invalide" });
            continue;
        }
        if (collaborators.includes(email)) {
            newcontacts.push({ data: { ...dataToReturn }, id, status: "excluded", note: "Un collaborateur est déjà défini avec cette adresse mail" });
            continue;
        }

        const contactDTO = contacts.filter(c => c.contact.email.toLowerCase().trim() === email)?.first();
        [status, note] = checkContact(dataToReturn, regex, contactDTO);
        const newcontact: ContactToImport = { data: { ...dataToReturn }, id, status, note, existedContact: contactDTO };
        if (newcontacts.findIndex(c => c.data.email === newcontact.data.email) !== -1) {
            newcontact.status = "excluded";
            newcontact.note = "Un contact avec un email identique est déjà présent dans la liste";
        }
        newcontacts.push(newcontact);
    }
    return newcontacts;
};

const COLUMN_ALIASES: Record<string, string[]> = {
    lastname: ["nom", "name", "lastname"],
    firstname: ["prenom", "firstname"],
    email: ["email", "mail"],
    phone: ["telephone portable", "portable", "mobile", "tel portable", "telephone mobile", "phone", "telephone", "tel"],
};

interface CsvColumnMapping {
    email: string;
    lastname: string;
    firstname: string;
    phone?: string;
}

const normalize = (str: string): string =>
    str.normalize("NFD").replace(/[\u0300-\u036f]/g, "")
       .toLowerCase()
       .trim()
       .replace(/\s+/g, " ");

const detectColumn = (headers: string[], aliases: string[]): string | undefined => {
    const normalizedHeaders = headers.map(normalize);
    const idx = normalizedHeaders.findIndex((h) => aliases.includes(h));
    return idx >= 0 ? headers[idx] : undefined;
};

const buildColumnMapping = (headers: string[]): CsvColumnMapping => {
    const email = detectColumn(headers, COLUMN_ALIASES.email);
    const lastname = detectColumn(headers, COLUMN_ALIASES.lastname);
    const firstname = detectColumn(headers, COLUMN_ALIASES.firstname);
    if (!$isstring(lastname) || !$isstring(email) || !$isstring(firstname)) {
        const missing = [!lastname && "Nom", !email && "Email"].filter(Boolean).join(", ");
        TSError.throw(`Données obligatoires manquantes : ${missing}`);
    }

    return {
        email: email!,
        lastname: lastname!,
        firstname: firstname!,
        phone: detectColumn(headers, COLUMN_ALIASES.phone),
    };
};

const decodeFileToText = async (file: File): Promise<string> => {
    const buffer = await file.arrayBuffer();
    const tscharset = detectedCharset(buffer);
    if (tscharset === null) {
        TSError.throw("Impossible de détecter le format du fichier d'importation - Veuillez importer le fichier au format UTF-8");
    }
    
    return tscharset.stringFromData(buffer);
};

const checkContact = (contact: NewPersonFormValues, regex: RegExp, existingContact?: ContactDto): [ImportContactStatus, string?]  => {
    if ($length(contact.lastName) === 0 || !regex.test(contact.lastName)) {
        return ['excluded', $length(contact.lastName) === 0 ? "Le nom de famille est obligatoire" : "Le nom de famille contient des caractères invalides"];
    }
    if ($ok(existingContact) && $ascii(existingContact!.contact.lastName.trim()).toUpperCase() !== $ascii(contact.lastName.trim()).toUpperCase()) {
        return ['warning', "Conflit avec un contact existant [nom de famille différent]"];
    }

    if ($length(contact.firstName) === 0 || !regex.test(contact.firstName!)) {
        return ['excluded', $length(contact.firstName) === 0 ? "Le prénom est obligatoire" : "Le prénom contient des caractères invalides"];
    }
    if ($ok(existingContact) && existingContact!.contact.firstName.toLowerCase().trim() !== contact.firstName.toLowerCase().trim()) {
        return ['warning', "Conflit avec un contact existant [prénom différent]"];
    }

    if ($length(contact.phone) > 0) {
        const phoneNumber = parsePhoneNumberFromString(contact.phone!, { defaultCountry: "FR" });
        const type = phoneNumber?.getType();
        const isMobile = type === "MOBILE" || type === "FIXED_LINE_OR_MOBILE";
        if (!phoneNumber?.isValid()) {
            return ['warning', "Le téléphone portable ne sera pas pris en compte [numéro invalide]"];
        }
        if (!isMobile) {
            return ['warning', "Le téléphone ne sera pas pris en compte [numéro fixe ou inconnu]"];
        }
        contact.phone = phoneNumber.format("E.164");
        if ($ok(existingContact) && existingContact!.contact.phone?.trim() !== contact.phone) {
            return ['warning', "Conflit avec un contact existant [numéro de téléphone différent]"];
        }
    }

    return $ok(existingContact) ? ['excluded', "Un contact avec les mêmes données existe déjà"] : ['ok'];
}